import { useState, type ReactNode } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card/Card';
import { useLabeling } from '../../context/useLabeling';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useDichotomousSearch } from '../../hooks/useDichotomousSearch';
import { currentIndex, type DichotomousPhase } from '../../dichotomous/dichotomousSearch';
import { ComparisonScreen } from './ComparisonScreen';
import { CompletionSummary } from './CompletionSummary';
import { ConflictDialog } from './ConflictDialog';

/** Phases where an image is on screen waiting for an OK / Worn / Unreadable answer. */
const QUESTION_PHASES: readonly DichotomousPhase[] = [
  'endpoint-lo',
  'endpoint-hi',
  'search',
  'verify',
];

/** Phases that end the run with a plan to confirm. Same order as `CompletionSummary`'s variants. */
type TerminalPhase = 'summary' | 'all-ok' | 'all-worn' | 'all-unreadable';
const TERMINAL_PHASES: readonly DichotomousPhase[] = [
  'summary',
  'all-ok',
  'all-worn',
  'all-unreadable',
];

interface DonePanelProps {
  onBackToModes(): void;
}

/**
 * Post-write acknowledgement. The search now covers the whole experiment in one pass, so a
 * finished run has nothing left to iterate to — the only way forward is back out of the mode.
 */
function DonePanel({ onBackToModes }: DonePanelProps) {
  const { t } = useTranslation();

  return (
    <div className="flex h-full w-full flex-col items-center justify-center p-4">
      <Card padding="lg" className="flex w-full max-w-md flex-col items-center gap-5 text-center">
        <h2 className="section-title">{t('labeling.dichotomous.done.title')}</h2>
        <Button type="button" onClick={onBackToModes}>
          {t('labeling.dichotomous.done.backToModes')}
        </Button>
      </Card>
    </div>
  );
}

/**
 * Orchestrator of the dichotomous mode: it owns the post-write state and routes the search
 * machine's phase to the one screen that phase calls for. It holds no search logic of its own
 * — every bracket, plan and transition comes from `useDichotomousSearch`, which spans the whole
 * experiment (every part shares the same tool, so there is one search, not one per part).
 */
export function DichotomousMode() {
  const { items, mode, setMode } = useLabeling();

  const [applying, setApplying] = useState(false);
  const [completed, setCompleted] = useState(false);

  const search = useDichotomousSearch();
  const { phase, state } = search;
  const isQuestionPhase = QUESTION_PHASES.includes(phase);

  useKeyboardShortcuts(
    {
      onLabel1: () => search.answer('ok'),
      onLabel2: () => search.answer('worn'),
      onLabel3: () => search.answer('unreadable'),
    },
    // A finished run still sits on a question-less screen: `1` / `2` / `3` must be inert there.
    mode === 'dichotomous' && isQuestionPhase && !completed
  );

  async function handleConfirm(): Promise<void> {
    setApplying(true);
    try {
      await (phase === 'summary' ? search.confirmBulk() : search.applyDegenerate());
      setCompleted(true);
    } catch {
      // The write path reports its own failures; keeping the summary on screen (rather than
      // claiming success) leaves the annotator able to retry.
    } finally {
      setApplying(false);
    }
  }

  /** Abandons the computed plan and hands the annotator over to focus mode. */
  function handleFallbackManual(): void {
    setMode('focus');
  }

  const total = state.keys.length;
  const index = currentIndex(state);

  function renderQuestion(): ReactNode {
    // Guaranteed non-`undefined` in a question phase; the guard only keeps a corrupted snapshot
    // from crashing the mode.
    if (!search.currentImage) return null;

    return (
      <ComparisonScreen
        variant={phase === 'verify' ? 'verify' : 'compare'}
        image={search.currentImage}
        position={{ index: index ?? 0, total }}
        progress={search.progress}
        bracket={{ lo: search.bracket.lo, hi: search.bracket.hi, total, current: index }}
        onAnswer={search.answer}
      />
    );
  }

  function renderConflict(): ReactNode {
    const conflict = search.conflict;
    if (!conflict) return null;

    // The screen behind the dialog shows the *conflicting* image, not a live question — there is
    // none. Without the image neither surface can render, so the mode degrades to empty.
    const conflictImage = items.find((item) => item.key === state.keys[conflict.index]);
    if (!conflictImage) return null;

    const position = { index: conflict.index, total };

    return (
      <>
        <ComparisonScreen
          // A conflict can only arise while verifying, so the frozen backdrop keeps that copy.
          variant="verify"
          image={conflictImage}
          position={position}
          progress={search.progress}
          bracket={{ lo: search.bracket.lo, hi: search.bracket.hi, total, current: null }}
          onAnswer={() => {}}
          disabled
        />
        <ConflictDialog
          open
          conflict={conflict}
          image={conflictImage}
          position={position}
          onResolve={search.resolveConflict}
        />
      </>
    );
  }

  function renderTerminal(): ReactNode {
    // An unconverged bracket in a terminal phase means a corrupted snapshot: show nothing rather
    // than a summary whose counts cannot be trusted.
    if (!search.plan) return null;

    return (
      <CompletionSummary
        plan={search.plan}
        variant={phase as TerminalPhase}
        onConfirm={() => void handleConfirm()}
        onFallbackManual={handleFallbackManual}
        onRestart={search.restart}
        applying={applying}
      />
    );
  }

  function renderPhase(): ReactNode {
    if (completed) {
      return <DonePanel onBackToModes={() => setMode('focus')} />;
    }
    if (isQuestionPhase) return renderQuestion();
    if (phase === 'conflict') return renderConflict();
    if (TERMINAL_PHASES.includes(phase)) return renderTerminal();
    // `empty`: no images in this experiment yet — nothing to ask, nothing to confirm.
    return null;
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto flex flex-col">
      <div className="flex-1 min-h-0 p-3 flex flex-col">{renderPhase()}</div>
    </div>
  );
}
